import * as React from 'react';
import { Button, HStack, VStack, Text, Divider, Spinner, Center } from 'native-base';
import MaterialIconByName from '../MaterialIconByName';

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { downloadImageToFs } from 'src/utils/download';
import { getText } from 'src/utils/i18n';

const min = Math.min(window.innerWidth, window.innerHeight);

const width = min;
const height = min;
const MAX_SCALE = 5;

export default function ZoomableImage({ imgSrc,
    imgHeight,
    imgWidth,
    cache,
    priority,
    timestamp
 }: {
        imgSrc: string | undefined,
        imgHeight: number,
        imgWidth: number,
        cache: boolean,
        priority: string,
        timestamp?: number | undefined
    }) {

    const [downloadingImage, setDownloadingImage] = React.useState(false);
    const [currentScale, setCurrentScale] = React.useState(1);
    const [imageLoading, setImageLoading] = React.useState(true);
    const [imageError, setImageError] = React.useState(false);

    React.useEffect(() => {
        setImageLoading(true);
        setImageError(false);
    }, [imgSrc]);

    const downloadImage = async () => {
        setDownloadingImage(true);
        try {
            if (imgSrc === undefined) {
                return;
            }

            await downloadImageToFs(imgSrc, "image");
        } finally {
            setDownloadingImage(false);
        }
    }

    if (imageError) {
        return (
            <VStack style={{ alignItems: 'center' }}>
                <Text fontSize="md">Failed to load image</Text>
                <Text fontSize="xs">{imgSrc}</Text>
            </VStack>
        );
    }

    return <TransformWrapper
        initialScale={1}
        minScale={1}
        maxScale={MAX_SCALE}
        initialPositionX={0}
        initialPositionY={0}
        onTransformed={(ref, state) => {
            setCurrentScale(state.scale);
        }}
    >
        {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
            <React.Fragment>
                <VStack style={{
                    alignItems: 'center',
                }}>
                    <HStack
                        style={{
                            justifyContent: 'center',
                            marginBottom: 4,
                            marginTop: 4,
                            flexWrap: 'wrap',
                            gap: 8,
                            alignItems: 'center'
                        }}
                        space={2}>
                        <HStack space={1}>
                            <Button
                                size="sm"
                                isDisabled={currentScale <= 1}
                                onPress={() => {
                                    zoomOut();
                                }}>
                                <MaterialIconByName name="minus" />
                            </Button>
                            <Button
                                size="sm"
                                isDisabled={currentScale >= MAX_SCALE}
                                onPress={() => {
                                    zoomIn();
                                }}>
                                <MaterialIconByName name="plus" />
                            </Button>
                            <Button
                                size="sm"
                                isDisabled={currentScale === 1}
                                onPress={() => {
                                    resetTransform();
                                }}>
                                <MaterialIconByName name="close" />
                            </Button>
                        </HStack>
                        <Text fontSize="sm" fontWeight="medium">
                            {Math.round(currentScale * 100)}%
                        </Text>
                        <Divider orientation="vertical" h="6" />
                        <Button
                            size="sm"
                            startIcon={<MaterialIconByName name="download" />}
                            isLoading={downloadingImage}
                            isLoadingText={getText('downloading')}
                            onPress={downloadImage}>
                            Download
                        </Button>
                    </HStack>
                    <TransformComponent
                        wrapperStyle={{ maxWidth: "100%", maxHeight: "calc(100vh - 50px)" }}>
                        {imageLoading && (
                            <Center style={{ position: 'absolute', width: '100%', height: '100%' }}>
                                <Spinner size="lg" />
                            </Center>
                        )}
                        <img
                            height={height}
                            style={{
                                maxHeight: "calc(100vh - 50px)",
                                maxWidth: "100%",
                                objectFit: "contain",
                                display: imageLoading ? 'none' : 'block'
                            }}
                            src={`${imgSrc}?timestamp=${timestamp}`}
                            onLoad={() => setImageLoading(false)}
                            onError={() => {
                                setImageLoading(false);
                                setImageError(true);
                            }}
                             />
                    </TransformComponent>
                </VStack>
            </React.Fragment >
        )
        }
    </TransformWrapper >
}
